/**
 * Ported from student-dashboard-v2/src/hooks/useTestProcessor.ts.
 * Combines placement tests and daily exercises into a single SimpleTest[] list
 * (placement tests first) with no type conversions — exactly like the student.
 */
import {useEffect, useState} from 'react';
import type {NewDailyExercise} from '@PikaDoApp/npm-pikado-logic';
import type {SimpleQuestion, SimpleTest} from './simpleTypes';

/** Lightweight placement test shape (from a curriculum's nested placementTest). */
export interface PreviewPlacementTest {
  _id: string;
  name?: string;
  description?: string;
  thumbnail?: string;
  createdBy?: string;
}

export const useTestProcessor = (
  dailyExercises: NewDailyExercise[] | undefined,
  placementTests: PreviewPlacementTest[] | undefined,
) => {
  const [tests, setTests] = useState<SimpleTest[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const processTests = () => {
      if (dailyExercises === undefined && placementTests === undefined) {
        setTests([]);
        setIsProcessing(true);
        return;
      }

      if (!dailyExercises && !placementTests) {
        setTests([]);
        setIsProcessing(false);
        return;
      }

      setIsProcessing(true);

      const allTests: SimpleTest[] = [];

      // Placement tests first (questions loaded on-demand when clicked).
      if (placementTests && placementTests.length > 0) {
        allTests.push(
          ...placementTests.map(pt => ({
            _id: pt._id,
            type: 'PLACEMENT_TEST' as const,
            name: pt.name,
            description: pt.description,
            thumbnail: pt.thumbnail,
            questions: [] as SimpleQuestion[],
            createdBy: pt.createdBy,
          })),
        );
      }

      // Daily exercises.
      if (dailyExercises && dailyExercises.length > 0) {
        allTests.push(
          ...dailyExercises.map((ex: any) => ({
            _id: ex._id,
            type: 'DAILY_EXERCISE' as const,
            name: ex.name,
            description: ex.description,
            summary: ex.summary,
            thumbnail: ex.thumbnail,
            questions: ex.questions as SimpleQuestion[] | undefined,
            media: ex.media,
            payload: ex.payload,
            createdAt: ex.createdAt,
            updatedAt: ex.updatedAt,
            createdBy: ex.createdBy,
            isDeleted: ex.isDeleted,
          })),
        );
      }

      setTests(allTests);
      setIsProcessing(false);
    };

    processTests();
  }, [dailyExercises, placementTests]);

  return {tests, isProcessing};
};
